const CONTACT_EMAIL = 'admin@jawabai.cloud';
const UPDATED = '2026-09-06';
// Privacy changed separately on 2026-10-18 (owner phone number).
const PRIVACY_UPDATED = '2026-10-18';

export const privacy = {
  en: {
    title: 'Privacy Policy',
    updated: PRIVACY_UPDATED,
    intro:
      'This Privacy Policy explains how the jawabai platform collects, uses, shares and protects information when you use the platform and the dashboard, AI agents and messaging channels, referred to collectively as the "Service". By using the Service, you agree to this Privacy Policy.',
    sections: [
      {
        heading: '1. Who is responsible for your data',
        paragraphs: [
          'For your account data, we are the data controller, as the platform providing the Service to you.',
          'For the conversations and content exchanged between your AI agent and your customers, you are responsible for that data and determine how it is used, while the platform handles it on your behalf and under your instructions.',
        ],
      },
      {
        heading: '2. Information we collect',
        list: [
          'Account information: email address and any business/profile details you provide through our identity provider (Keycloak) when you register.',
          'Phone number: the phone number and country you give when you register or later in the dashboard Settings. It is not verified yet and is not shown to your customers.',
          'Content you upload: documents, price lists, FAQs and other files you add to train your agent, and the manual answers you approve.',
          'Conversations: messages exchanged between your agent and your end-customers, including the channel identifier (e.g. Telegram user ID) used to route replies.',
          'Usage and billing data: message counts, plan and subscription status, and payment references from our payment provider (ShamCash). We do not store full card or wallet credentials.',
          'Technical data: logs, request trace identifiers, IP address and timestamps used for security, debugging and abuse prevention.',
        ],
      },
      {
        heading: '3. How we use information',
        list: [
          'To operate the Service: answer your customers’ questions from your documents, route notifications to you, and (where enabled) handle appointment requests.',
          'To enforce plan limits, process subscriptions and send service emails (welcome, renewal, usage and expiry notices).',
          'To contact you about your account and support requests using your phone number, including by call or WhatsApp. Later we may use it to send service alerts by SMS or WhatsApp; before that we will ask you to confirm the number with a code.',
          'To secure the Service, prevent abuse and free-trial farming, and troubleshoot problems.',
          'We do not sell your data, and we do not use your documents or conversations to train shared or third-party foundation models.',
        ],
      },
      {
        heading: '4. AI processing and sub-processors',
        paragraphs: [
          'To generate answers, relevant portions of your documents and the current conversation are sent to large-language-model providers (currently Groq and Google Gemini) strictly to produce a response for that request.',
          'We also rely on trusted service providers to run jawabai:',
        ],
        list: [
          'Keycloak — authentication and account identity.',
          'Groq and Google Gemini — AI answer generation.',
          'Telegram — the messaging channel your customers use.',
          'Resend — transactional and notification emails.',
          'ShamCash — payment processing for subscriptions.',
          'Our hosting infrastructure — servers running PostgreSQL (with pgvector), Redis and RabbitMQ.',
        ],
      },
      {
        heading: '5. Data isolation and security',
        paragraphs: [
          'The Service is designed so that each customer’s data is isolated from other customers’ data, and access and queries are limited to each customer’s own data. Sensitive data, such as messaging bot tokens, is encrypted at rest using AES-256, and access to system data is limited to authorised people.',
          'No method of transmitting or storing data guarantees complete security, so we cannot guarantee absolute protection of data. However, we use a range of security measures to protect and safeguard your data.',
        ],
      },
      {
        heading: '6. Data retention',
        paragraphs: [
          'We keep your account information for as long as your account is active. We also keep conversations and uploaded documents to operate your agent. You can delete uploaded documents in the dashboard. For conversation deletion or account closure, email us; we process the request within a reasonable period, subject to legal or accounting obligations.',
        ],
      },
      {
        heading: '7. Your rights',
        paragraphs: [
          'You can view and correct available account information and remove uploaded documents in the dashboard. Data export, conversation deletion and account deletion are handled by request; email us at the address below. These actions are not currently self-service.',
        ],
      },
      {
        heading: '8. Data of your end-customers',
        paragraphs: [
          'When your customers contact your agent, you are responsible for explaining how their data is used and for making sure there is a lawful basis to process it. The platform uses this data only to the extent needed to provide the Service to you.',
        ],
      },
      {
        heading: '9. Changes to this Policy',
        paragraphs: [
          'We may make changes to this Privacy Policy from time to time. If the changes are material, we will update the "Last updated" date and notify you where needed.',
        ],
      },
      {
        heading: '10. Contact',
        paragraphs: [`Questions about privacy? Email us at ${CONTACT_EMAIL}.`],
      },
    ],
  },
  ar: {
    title: 'سياسة الخصوصية',
    updated: PRIVACY_UPDATED,
    intro:
      'توضح سياسة الخصوصية هذه كيفية جمع منصة jawabai المعلومات واستخدامها ومشاركتها وحمايتها عند استخدامك للمنصة ولوحة التحكم والوكلاء الأذكياء وقنوات المراسلة، ويُشار إليها مجتمعةً بـ«الخدمة». وباستخدامك للخدمة، فإنك توافق على سياسة الخصوصية هذه.',
    sections: [
      {
        heading: '١. من المسؤول عن بياناتك',
        paragraphs: [
          'بالنسبة إلى بيانات حسابك، نكون نحن المتحكّم في البيانات بصفتنا المنصة التي تقدم الخدمة لك.',
          'أما المحادثات والمحتوى الذي يتم تبادله بين وكيلك الذكي وعملائك، فأنت المسؤول عن هذه البيانات وتحدد كيفية استخدامها، بينما تتعامل المنصة معها نيابةً عنك ووفقًا لتعليماتك.',
        ],
      },
      {
        heading: '٢. المعلومات التي نجمعها',
        list: [
          'معلومات الحساب: البريد الإلكتروني وأي تفاصيل نشاط أو ملف شخصي تقدّمها عبر مزوّد الهوية (Keycloak) عند التسجيل.',
          'رقم الهاتف: رقم الهاتف والدولة اللذان تقدّمهما عند التسجيل أو لاحقاً من الإعدادات في لوحة التحكم. لا يتم التحقق منه حالياً، ولا يظهر لعملائك.',
          'المحتوى الذي ترفعه: المستندات وقوائم الأسعار والأسئلة الشائعة والملفات الأخرى التي تضيفها لتدريب وكيلك، والإجابات اليدوية التي تعتمدها.',
          'المحادثات: الرسائل المتبادلة بين وكيلك وعملائك، بما في ذلك معرّف القناة (مثل معرّف مستخدم تيليجرام) المستخدَم لتوجيه الردود.',
          'بيانات الاستخدام والفوترة: عدد الرسائل، وحالة الخطة والاشتراك، ومراجع الدفع من مزوّد الدفع (ShamCash). لا نخزّن بيانات البطاقات أو المحافظ كاملةً.',
          'البيانات التقنية: السجلّات ومعرّفات تتبّع الطلبات وعنوان IP والطوابع الزمنية، لأغراض الأمان وتصحيح الأخطاء ومنع إساءة الاستخدام.',
        ],
      },
      {
        heading: '٣. كيف نستخدم المعلومات',
        list: [
          'لتشغيل الخدمة: الإجابة على أسئلة عملائك من مستنداتك، وإرسال الإشعارات إليك، ومعالجة طلبات الحجز حين تكون مُفعّلة.',
          'لتطبيق حدود الخطة، ومعالجة الاشتراكات، وإرسال رسائل الخدمة (ترحيب، تجديد، تنبيهات الاستخدام والانتهاء).',
          'للتواصل معك بخصوص حسابك وطلبات الدعم عبر رقم هاتفك، بما في ذلك الاتصال أو واتساب. وقد نستخدمه لاحقاً لإرسال تنبيهات الخدمة عبر الرسائل القصيرة أو واتساب؛ وقبل ذلك سنطلب منك تأكيد الرقم برمز تحقق.',
          'لتأمين الخدمة ومنع إساءة الاستخدام واستغلال التجربة المجانية، ومعالجة المشكلات.',
          'لا نبيع بياناتك، ولا نستخدم مستنداتك أو محادثاتك لتدريب نماذج ذكاء اصطناعي مشتركة أو تابعة لأطراف أخرى.',
        ],
      },
      {
        heading: '٤. المعالجة بالذكاء الاصطناعي والجهات المعالِجة',
        paragraphs: [
          'لتوليد الإجابات، تُرسَل الأجزاء ذات الصلة من مستنداتك ومن المحادثة الحالية إلى مزوّدي نماذج اللغة الكبيرة (حالياً Groq وGoogle Gemini) لغرض إنتاج الردّ على ذلك الطلب فقط.',
          'كما نعتمد على مزوّدي خدمات موثوقين لتشغيل جواب AI:',
        ],
        list: [
          'Keycloak — المصادقة وهوية الحساب.',
          'Groq وGoogle Gemini — توليد الإجابات بالذكاء الاصطناعي.',
          'Telegram — قناة المراسلة التي يستخدمها عملاؤك.',
          'Resend — رسائل البريد الخاصة بالمعاملات والإشعارات.',
          'ShamCash — معالجة مدفوعات الاشتراكات.',
          'بنيتنا التحتية للاستضافة — خوادم تشغّل PostgreSQL (مع pgvector) وRedis وRabbitMQ.',
        ],
      },
      {
        heading: '٥. عزل البيانات والأمان',
        paragraphs: [
          'الخدمة مصممة بحيث تكون بيانات كل عميل معزولة عن بيانات العملاء الآخرين، وتقتصر عمليات الوصول والاستعلام على البيانات الخاصة بكل عميل. كما يتم تشفير البيانات الحساسة، مثل رموز بوتات المراسلة، أثناء التخزين باستخدام AES-256، ويقتصر الوصول إلى بيانات النظام على الأشخاص المصرح لهم.',
          'لا توجد وسيلة لنقل أو تخزين البيانات تضمن الأمان بشكل كامل، لذلك لا يمكننا ضمان حماية مطلقة للبيانات. ومع ذلك، نستخدم مجموعة من الإجراءات الأمنية لحماية بياناتك والحفاظ عليها.',
        ],
      },
      {
        heading: '٦. الاحتفاظ بالبيانات',
        paragraphs: [
          'نحتفظ بمعلومات حسابك طالما أن حسابك نشط، كما نحتفظ بالمحادثات والمستندات المرفوعة لتشغيل وكيلك. يمكنك حذف المستندات المرفوعة من لوحة التحكم. ولحذف المحادثات أو إغلاق الحساب، راسلنا عبر البريد الإلكتروني؛ ونعالج الطلب خلال فترة مناسبة مع مراعاة الالتزامات القانونية أو المحاسبية.',
        ],
      },
      {
        heading: '٧. حقوقك',
        paragraphs: [
          'يمكنك عرض معلومات الحساب المتاحة وتصحيحها وحذف المستندات المرفوعة من لوحة التحكّم. أما تصدير البيانات وحذف المحادثات وحذف الحساب فتتم عبر طلب ترسله إلى عنوان البريد أدناه، إذ لا تتوفر هذه الإجراءات ذاتياً حالياً.',
        ],
      },
      {
        heading: '٨. بيانات عملائك',
        paragraphs: [
          'عندما يتواصل عملاؤك مع وكيلك، تكون مسؤولاً عن توضيح كيفية استخدام بياناتهم والتأكد من وجود أساس قانوني لمعالجتها. وتستخدم المنصة هذه البيانات فقط بالقدر اللازم لتقديم الخدمة لك.',
        ],
      },
      {
        heading: '٩. التعديلات على هذه السياسة',
        paragraphs: [
          'قد نُجري تعديلات على سياسة الخصوصية من وقت لآخر. وفي حال كانت التعديلات مهمة، سنحدّث تاريخ «آخر تحديث» ونبلغك بها عند الحاجة.',
        ],
      },
      {
        heading: '١٠. التواصل',
        paragraphs: [`لأي استفسار حول الخصوصية، راسلنا على ${CONTACT_EMAIL}.`],
      },
    ],
  },
};

export const terms = {
  en: {
    title: 'Terms of Service',
    updated: UPDATED,
    intro:
      'These Terms of Service govern your access to and use of the jawabai.cloud platform and the related dashboard, AI agents and integrations provided by the platform. By creating an account you agree to these Terms.',
    sections: [
      {
        heading: '1. The Service',
        paragraphs: [
          'The jawabai platform lets businesses create agents that answer customer questions based on the business’s documents and connect to messaging channels such as Telegram or their website. Features may be updated or added to the platform from time to time, and some features are not yet available and are marked "coming soon" — such as appointment booking — until they are officially released.',
        ],
      },
      {
        heading: '2. Accounts and eligibility',
        paragraphs: [
          'You undertake to provide accurate information and keep your login credentials confidential, and you are responsible for the activity on your account. Each verified user is entitled to one free trial only; creating multiple accounts to obtain additional trials is prohibited.',
        ],
      },
      {
        heading: '3. Free trial, subscriptions and billing',
        paragraphs: [
          'Every new account receives a 14-day free trial, after which access to the Service stops unless you subscribe to a paid plan. Subscriptions are paid through the payment provider ShamCash for the plan’s period, and access continues until it ends. Each plan’s limits, such as the number of messages and agents, are applied automatically. Payments are non-refundable unless the law provides otherwise, and prices may be changed with prior notice.',
        ],
      },
      {
        heading: '4. Acceptable use',
        paragraphs: ['The Service may not be used to:'],
        list: [
          'Send spam, or harass, deceive or defraud others.',
          'Publish or upload any unlawful content or content that infringes the rights of others.',
          'Attempt to access other users’ data or accounts, or bypass the platform’s protection and isolation systems.',
          'Attempt to breach the Service, reverse engineer it, disrupt it, or affect its performance.',
          'Use the Service in violation of applicable laws and regulations, or the terms of connected platforms such as Telegram.',
        ],
      },
      {
        heading: '5. Your content',
        paragraphs: [
          'Ownership of the documents and content you upload and of your customers’ conversations remains yours. You grant us only the permissions needed to store and process that content so the Service can be operated and provided to you. You are responsible for making sure you have the right to use the content and upload it to the platform.',
        ],
      },
      {
        heading: '6. Suspension and termination',
        paragraphs: [
          'We may suspend or terminate accounts that violate these Terms, abuse the Service, or create risk for us or other users. You may stop using the Service at any time and request account deletion by emailing us.',
        ],
      },
      {
        heading: '7. Disclaimers and limitation of liability',
        paragraphs: [
          'We provide the Service as is, and we do not warrant that it will always be available or error-free, to the extent permitted by law. If a problem relating to the Service occurs, our liability is limited to the amounts you paid us in the 3 months before the problem. We are not liable for any indirect or incidental damages, or damages resulting from use of the Service.',
        ],
      },
      {
        heading: '8. Changes to these Terms',
        paragraphs: [
          'We may amend these Terms from time to time, and if we make material changes we will update the "Last updated" date. Your continued use of the Service after the amendments are made is considered acceptance of the new Terms.',
        ],
      },
      {
        heading: '9. Contact',
        paragraphs: [`Questions about these Terms? Email us at ${CONTACT_EMAIL}.`],
      },
    ],
  },
  ar: {
    title: 'الشروط والأحكام',
    updated: UPDATED,
    intro:
      'تحكم هذه الشروط والأحكام وصولك إلى منصة jawabai.cloud ولوحة التحكّم والوكلاء الأذكياء وعمليات التكامل المقدَّمة من المنصة واستخدامك لها. بإنشائك حساباً فإنك توافق على هذه الشروط.',
    sections: [
      {
        heading: '١. الخدمة',
        paragraphs: [
          'تتيح منصة jawabai للأنشطة التجارية إنشاء وكلاء يجيبون على أسئلة العملاء استناداً إلى مستندات النشاط، ويتصلون بقنوات المراسلة مثل تيليجرام أو الموقع الالكتروني، قد يتم تحديث أو إضافة ميزات جديدة إلى المنصة من وقت لآخر، وقد تكون بعض الميزات غير متاحة بعد ويُشار إليها بعبارة «قريباً»، مثل ميزة حجز المواعيد، إلى حين إطلاقها رسمياً.',
        ],
      },
      {
        heading: '٢. الحسابات والأهلية',
        paragraphs: [
          'يلتزم المستخدم بتقديم معلومات صحيحة والحفاظ على سرية بيانات الدخول، ويتحمّل مسؤولية نشاط حسابه. يحق لكل مستخدم موثّق تجربة مجانية واحدة فقط، ويُمنع إنشاء حسابات متعددة للحصول على تجارب إضافية.',
        ],
      },
      {
        heading: '٣. التجربة المجانية والاشتراكات والفوترة',
        paragraphs: [
          'يحصل كل حساب جديد على تجربة مجانية لمدة ١٤ يوماً، وبعد انتهائها يتوقف الوصول إلى الخدمة ما لم يتم الاشتراك في خطة مدفوعة. تُدفع الاشتراكات عبر مزوّد الدفع ShamCash وفق مدة الخطة، ويستمر الوصول حتى انتهائها. تطبق حدود كل خطة، مثل عدد الرسائل والوكلاء، تلقائياً. المدفوعات غير قابلة للاسترداد إلا إذا نص القانون على خلاف ذلك، ويجوز تعديل الأسعار مع إشعار مسبق.',
        ],
      },
      {
        heading: '٤. الاستخدام المقبول',
        paragraphs: ['يُمنع استخدام الخدمة في:'],
        list: [
          'إرسال رسائل مزعجة أو مضايقة الآخرين أو خداعهم أو الاحتيال عليهم.',
          'نشر أو رفع أي محتوى غير قانوني أو ينتهك حقوق الغير.',
          'محاولة الوصول إلى بيانات أو حسابات مستخدمين آخرين أو تجاوز أنظمة الحماية والعزل في المنصة.',
          'محاولة اختراق الخدمة أو إجراء هندسة عكسية لها أو تعطيلها أو التأثير على أدائها.',
          'استخدام الخدمة بما يخالف القوانين والأنظمة المعمول بها أو شروط المنصات المرتبطة بها، مثل تيليجرام.',
        ],
      },
      {
        heading: '٥. المحتوى الخاص بك',
        paragraphs: [
          'تبقى ملكية المستندات والمحتوى الذي ترفعه ومحادثات عملائك لك. وتمنحنا فقط الصلاحيات اللازمة لتخزين هذا المحتوى ومعالجته بما يتيح تشغيل الخدمة وتقديمها لك. وتتحمل مسؤولية التأكد من امتلاكك الحق في استخدام المحتوى ورفعه إلى المنصة.',
        ],
      },
      {
        heading: '٦. التعليق والإنهاء',
        paragraphs: [
          'يجوز لنا تعليق أو إنهاء الحسابات التي تخالف هذه الشروط أو تسيء استخدام الخدمة أو تشكّل خطراً علينا أو على مستخدمين آخرين. ويمكنك التوقّف عن استخدام الخدمة في أي وقت وطلب حذف الحساب عبر مراسلتنا بالبريد الإلكتروني.',
        ],
      },
      {
        heading: '٧. إخلاء المسؤولية وحدودها',
        paragraphs: [
          'نقدّم الخدمة كما هي، ولا نضمن أنها ستكون متاحة أو خالية من الأخطاء بشكل دائم، وذلك ضمن الحدود التي يسمح بها القانون. وإذا حدثت مشكلة تتعلق بالخدمة، فإن مسؤوليتنا تقتصر على المبالغ التي دفعتها لنا خلال الأشهر الثلاثة السابقة للمشكلة. ولا نتحمل مسؤولية أي أضرار غير مباشرة أو عرضية أو ناتجة عن استخدام الخدمة.',
        ],
      },
      {
        heading: '٨. التعديلات على هذه الشروط',
        paragraphs: [
          'قد نقوم بتعديل هذه الشروط من وقت لآخر، وفي حال إجراء تغييرات مهمة سنحدّث تاريخ «آخر تحديث». ويُعتبر استمرارك في استخدام الخدمة بعد إجراء التعديلات موافقةً على الشروط الجديدة.',
        ],
      },
      {
        heading: '٩. التواصل',
        paragraphs: [`لأي استفسار حول هذه الشروط، راسلنا على ${CONTACT_EMAIL}.`],
      },
    ],
  },
};
